import React, { useMemo } from 'react';
import { fromJS } from 'immutable';

import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectExtensions } from 'flow/selectors/ExtensionSelectors';
import { selectMembers } from 'flow/selectors/TicketsSelectors';
import { ExtensionKey, TicketKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { sortByName } from 'flow/utils';
import AccessMemberItem from './AccessMemberItem';

interface ExtensionReduced {
    key: ExtensionKey;
}

interface Props {
    ticketKey: TicketKey;
    canEdit: boolean;
}

/**
 * Who the ticket's members are, and the way out of being one.
 *
 * The list shrinks past its content rather than capping at a row count, so a long
 * list gives way to the general-access section below it instead of pushing that
 * out of the dialog.
 */
const TicketAccessMembers = ({ ticketKey, canEdit }: Props) => {
    const { i18next } = useAppContext();

    const memberKeys = useAppSelector((state) =>
        selectMembers(state, ticketKey),
    );
    const extensions = useAppSelector(selectExtensions);

    const membersSorted = useMemo(() => {
        if (!memberKeys) return undefined;

        /**
         * Built from the member keys rather than by filtering extensions, so a member who
         * has left the customer still gets a row — and can therefore still be removed.
         */
        return memberKeys
            .toList()
            .map((key) =>
                fromJS({
                    key: key,
                    contact: extensions.getIn([key, 'contact']),
                }),
            )
            .sort(sortByName);
    }, [extensions, memberKeys]);

    return (
        <div className="flex min-h-0 flex-col gap-y-2">
            <span className="shrink-0 text-sm font-bold text-gray-900">
                {i18next.t('tickets.access.members', {
                    count: memberKeys?.size ?? 0,
                })}
            </span>

            <ul className="scrollbar scrollbar-contrast scrollbar-gutter-stable min-h-0">
                {membersSorted
                    ?.map((extension: TypedMap<ExtensionReduced>) => (
                        <AccessMemberItem
                            key={`access-member-${extension.get('key')}`}
                            memberKey={extension.get('key')}
                            ticketKey={ticketKey}
                            canEdit={canEdit}
                        />
                    ))
                    .toArray()}
            </ul>
        </div>
    );
};

export default TicketAccessMembers;
